import { useSyncExternalStore } from "react";
import { ON_PORTAL } from "./portal-env";

// Reactive media-query hooks for the in-game layout. Unlike a mount-time
// isMobile() read, these re-render on change, so resizing the window switches
// the layout live (no frozen mobile/desktop mismatch where the game vanishes).

// Keep this in sync with $bp-lg in styles/variables.scss - the in-game layout
// goes "compact" at this width and the CSS keys off the same value. A short
// window goes compact too, whatever its width (a laptop, a game site's 16:9
// frame): the roomy layout's bar above the board and the scores under it
// would take a third of its height, and the board would shrink with it.
const COMPACT_MAX_PX = 1024;
const COMPACT_MAX_H_PX = 760;

function makeMediaStore(query: string) {
  function subscribe(cb: () => void): () => void {
    if (typeof window === "undefined" || !window.matchMedia) return () => {};
    const mql = window.matchMedia(query);
    // Safari <14 only supports addListener/removeListener.
    if (mql.addEventListener) {
      mql.addEventListener("change", cb);
      return () => mql.removeEventListener("change", cb);
    }
    mql.addListener(cb);
    return () => mql.removeListener(cb);
  }
  function getSnapshot(): boolean {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  }
  return { subscribe, getSnapshot };
}

const compactStore = makeMediaStore(`(max-width: ${COMPACT_MAX_PX}px), (max-height: ${COMPACT_MAX_H_PX}px)`);
const touchStore = makeMediaStore("(pointer: coarse)");

// True when the viewport is narrow (or short) enough that the in-game layout
// should use the compact (centered arena + overlay score) treatment instead of
// the 3-column stage. Live, so it's predictable when it kicks in.
export function useIsCompact(): boolean {
  return useSyncExternalStore(compactStore.subscribe, compactStore.getSnapshot, () => false);
}

// True on touch devices (coarse pointer). Decoupled from layout width: it only
// gates touch steering controls, so a wide touch laptop keeps the roomy layout
// and a narrow mouse window still goes compact without touch zones.
export function useIsTouch(): boolean {
  return useSyncExternalStore(touchStore.subscribe, touchStore.getSnapshot, () => false);
}

// A phone on its side (the only way phones play): coarse pointer, landscape,
// and short. The section pages turn into full-screen game-app screens there
// (see app/SectionModal.tsx). Keep the query in sync with section-app.scss.
// On a game site's build it is the game app everywhere (shared/portal-env.ts).
const landscapePhoneStore = makeMediaStore("(pointer: coarse) and (orientation: landscape) and (max-height: 500px)");
export function useLandscapePhone(): boolean {
  const phone = useSyncExternalStore(landscapePhoneStore.subscribe, landscapePhoneStore.getSnapshot, () => false);
  return ON_PORTAL || phone;
}
